<script lang="ts" setup>
import { ref, computed, watch, VNode } from 'vue';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOptions,
  ComboboxOption,
  TransitionRoot,
} from '@headlessui/vue';

import { CheckmarkIcon, CaretDownIcon } from '@/components/svg';
import { InputVariant, IOptions } from '@/types';

interface Props {
  label?: string;
  placeholder?: string;
  labelIcon?: string | object | (() => VNode) | null;
  required?: boolean;
  variant?: InputVariant;
  modelValue?: string | number;
  options?: IOptions[];
  testId?: string;
  loadingOptions?: boolean;
  loadingMoreOptions?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  label: "",
  placeholder: "",
  labelIcon: null,
  required: false,
  variant: "primary",
  modelValue: "",
  options: () => [],
  testId: "search-select",
  loadingOptions: false,
  loadingMoreOptions: false,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number): void;
  (e: "update:query", value: string): void;
  (e: "load-more-options"): void;
}>();

const selected = ref<IOptions | null>(null);
const query = ref<string>('');
const optionsRef = ref<HTMLElement | null>(null);

const variantClass = computed(() => props.variant ?? 'primary');
const backgroundStyle = computed(() => ({
  background:
    selected.value && ["primary", "admin"].includes(props.variant)
      ? "rgba(28, 86, 145, 0.05)"
      : "none",
}));

const filteredOptions = computed(() =>
  query.value === ''
    ? props.options
    : props.options.filter((item) =>
        item.label
          .toLowerCase()
          .replaceAll(/\s+/g, '')
          .includes(query.value.toLowerCase().replaceAll(/\s+/g, ''))
      )
);

const handleQueryChange = (event: Event) => {
  const value = (event.target as HTMLInputElement).value;
  query.value = value;
  emit('update:query', value);
};

const handleScroll = (event: Event) => {
  const target = event.target as HTMLElement;
  const scrollTop = target.scrollTop;
  const scrollHeight = target.scrollHeight;
  const clientHeight = target.clientHeight;
  
  // Check if scrolled to bottom (with 10px threshold)
  if (scrollTop + clientHeight >= scrollHeight - 10 && !props.loadingMoreOptions && !props.loadingOptions) {
    emit('load-more-options');
  }
};

watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    const foundOption = props.options.find(opt => opt.value === newValue);
    if (foundOption) {
      selected.value = foundOption;
    }
  } else {
    selected.value = null;
  }
}, { immediate: true });

watch(selected, (newValue) => {
  if (newValue) {
    emit('update:modelValue', newValue.value);
  } else {
    emit('update:modelValue', '');
  }
});
</script>

<template>
  <div :class="['search-select w-100', variantClass]">
    <Combobox v-model="selected" by="value">
      <ComboboxLabel v-if="label" class="combobox-label d-flex align-items-center">
        {{ label }}
        <span v-if="required">&ast;</span>
        <component v-if="labelIcon" :is="labelIcon" class="ms-1" />
      </ComboboxLabel>

      <div class="w-100 h-100 position-relative">
        <div class="combobox-input-wrapper" :style="backgroundStyle">
          <ComboboxInput
            class="combobox-input"
            :displayValue="(item) => (item as IOptions)?.label || ''"
            @change="handleQueryChange"
            :placeholder="placeholder"
            :data-testid="testId"
            v-bind="$attrs"
          />
          <ComboboxButton class="combobox-button">
            <CaretDownIcon class="caret-icon" aria-hidden="true" />
          </ComboboxButton>
        </div>

        <TransitionRoot
          leave="transition-leave"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          @after-leave="() => {
            query = ''
            emit('update:query', '')
          }"
        >
          <ComboboxOptions
            class="combobox-options" 
            ref="optionsRef"
            @scroll="handleScroll"
          >
            <div v-if="loadingOptions" class="loading-options-container">
              <div class="spinner"></div>
              <span class="loading-options-text">Loading...</span>
            </div>

            <div
              v-else-if="filteredOptions.length === 0 && query !== ''"
              class="no-results"
            >
              Nothing found.
            </div>

            <template v-else>
              <ComboboxOption
                v-for="item in filteredOptions"
                as="template"
                :key="item.value"
                :value="item"
                v-slot="{ selected, active }"
              >
                <li
                  class="combobox-option"
                  :class="{ active, selected }"
                >
                  <span class="option-text" :class="{ selected }">
                    {{ item.label }}
                  </span>
                  <span v-if="selected" class="checkmark-wrapper">
                    <CheckmarkIcon class="checkmark-icon" aria-hidden="true" />
                  </span>
                </li>
              </ComboboxOption>

              <div v-if="loadingMoreOptions" class="loading-more-options-container">
                <div class="spinner-small"></div>
                <span class="loading-options-text-small">Loading more...</span>
              </div>
            </template>
          </ComboboxOptions>
        </TransitionRoot>
      </div>
    </Combobox>
  </div>
</template>

<style scoped>
.search-select .combobox-label {
  margin-bottom: 0.25rem;
  font-size: var(--font-xs);
  font-weight: 400;
  text-transform: capitalize;
}

.combobox-input-wrapper {
  position: relative;
  width: 100%;
  height: 2.5rem;
  max-height: 2.5rem;
  cursor: default;
  overflow: hidden;
}

.combobox-input {
  width: 100%;
  height: 100%;
  padding: 0.25rem 0.5rem;
  font-size: var(--font-sm);
  font-weight: 400;
  border: none;
  background: transparent;
}

.combobox-input:focus {
  box-shadow: 0 0 0 2px color-mix(in srgb, currentColor 10%, transparent);
  outline: none;
}

.combobox-input::placeholder {
  color: rgba(28, 86, 145, 0.2);
  font-size: var(--font-xs);
}

.combobox-input:disabled {
  color: var(--ch-black-50);
  opacity: 0.75;
}

.combobox-button {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding-right: 0.5rem;
  background: transparent;
  border: none;
  cursor: pointer;
}

.caret-icon {
  width: 0.875rem;
  height: 0.875rem;
  color: var(--ch-black);
}

.transition-leave {
  transition: opacity 100ms ease-in;
}

.combobox-options {
  position: absolute;
  z-index: 10;
  margin-top: 0.125rem;
  width: 100%;
  max-height: 12rem;
  overflow: auto;
  background: var(--ch-black-70);
  backdrop-filter: blur(2px);
  padding: 0.25rem 0.125rem;
  border-radius: 6px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  list-style: none;
}

.no-results {
  position: relative;
  cursor: default;
  user-select: none;
  padding: 0.25rem 1rem;
  color: var(--ch-light-grey);
  font-size: var(--font-sm);
}

.combobox-option {
  position: relative;
  cursor: pointer;
  user-select: none;
  padding: 0.5rem 1rem 0.5rem 1.5rem;
  color: var(--ch-white);
  border-radius: 8px;
}

.combobox-option.active {
  background-color: var(--ch-blue-100);
}

.option-text {
  font-size: var(--font-sm);
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-text.selected {
  font-weight: 500;
}

.checkmark-wrapper {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  padding-left: 0.5rem;
}

.combobox-option.active .checkmark-wrapper {
  color: var(--ch-white);
}

.checkmark-icon {
  height: 0.75rem;
  width: 0.75rem;
}

/* Loading States */
.loading-options-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  gap: 0.5rem;
}

.loading-more-options-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  gap: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 0.25rem;
}

.loading-options-text {
  color: var(--ch-light-grey);
  font-size: var(--font-sm);
}

.loading-options-text-small {
  color: var(--ch-light-grey);
  font-size: var(--font-xs);
}

@media screen and (min-width: 1024px) {
  .search-select .combobox-label {
    font-size: var(--font-sm);
  }
}

/* Variant Styles */

/* Primary Variant */
.search-select.primary .combobox-label {
  color: var(--ch-black);
}

.search-select.primary .combobox-input-wrapper {
  color: var(--ch-black);
  border: 1px solid var(--ch-blue-100);
  border-radius: 6px;
}

.search-select.primary .combobox-input {
  color: var(--ch-black);
  border-radius: 6px;
}

.search-select.primary .combobox-input:focus {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ch-blue-100) 15%, transparent);
}

/* AdminV1 Variant */
.search-select.admin .combobox-label {
  color: var(--ch-text-head);
}

.search-select.admin .combobox-input-wrapper {
  color: var(--ch-text-head);
  border: 1px solid var(--ch-input);
  border-radius: 5px;
}

.search-select.admin .combobox-input {
  color: var(--ch-text-head);
  border-radius: 5px;
}

/* Grey Variant */
.search-select.grey .combobox-label {
  color: var(--ch-black);
}

.search-select.grey .combobox-input-wrapper {
  color: var(--ch-black);
  border: 1px solid var(--ch-border-grey-30);
  border-radius: 6px;
}

.search-select.grey .combobox-input {
  color: var(--ch-black);
  border-radius: 6px;
}

.search-select.grey .combobox-input::placeholder {
  color: var(--ch-border-grey);
}

.search-select.grey .combobox-input:focus {
  border: 2px solid var(--ch-blue-100);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ch-blue-100) 10%, transparent);
}

/* ReadOnly Variant */
.search-select .combobox-input[readonly]::placeholder {
  color: var(--ch-black) !important; 
  font-size: var(--font-sm) !important;
}

.search-select .combobox-input-wrapper:has(.combobox-input[readonly]) { 
  border: 1px solid var(--ch-border-grey-30) !important; 
  border-radius: 6px !important;
  color: var(--ch-black) !important; 
  font-size: var(--font-sm) !important;
  background-color: var(--ch-readonly) !important;
}

.search-select .combobox-input[readonly]:focus {
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ch-readonly) 15%, transparent) !important;
  outline: none !important;
  border: none !important;
}
</style>